import { BRAND_ICONS } from './brandIcons';
import type { Brand, BrandTheme } from './types';

/** every CSS var a theme key feeds: the Tailwind tokens plus the Tabler ones older components still read */
export const THEME_VARS: Record<keyof BrandTheme, string[]> = {
  primary: ['--tw-color-primary', '--tblr-primary'],
  primaryHover: ['--tw-color-primary-darken'],
  primarySoft: ['--tw-color-primary-lt', '--tblr-primary-lt'],
  accent: ['--tw-color-brand-accent'],
  accentOnDark: ['--tw-color-brand-accent-lt'],
  accentHover: ['--tw-color-brand-accent-hover'],
  accentText: ['--tw-color-brand-accent-fg'],
  sidebarBg: ['--tw-color-sidebar-bg'],
  panelBg: ['--tw-color-panel-bg'],
  sidebarLink: ['--tw-color-sidebar-link'],
  pageBg: ['--tw-color-body-bg', '--tblr-body-bg'],
  text: ['--tw-color-body', '--tblr-body-color'],
  border: ['--tw-color-border', '--tblr-border-color'],
};

// Tabler composes its translucent primaries from an rgb triplet
function hexToRgbTriplet(hex: string): string | null {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  return match
    ? match
        .slice(1)
        .map((part) => parseInt(part, 16))
        .join(', ')
    : null;
}

function applyFont(brand: Brand) {
  if (!brand.font) return;

  if (brand.font.file) {
    const name = brand.font.family.split(',')[0].trim().replace(/['"]/g, '');
    const style = document.createElement('style');
    style.textContent = `@font-face{font-family:'${name}';src:url('${brand.font.file}') format('woff2');font-weight:100 900;font-display:swap}`;
    document.head.appendChild(style);
  }

  const root = document.documentElement.style;
  root.setProperty('--font-brand', brand.font.family);
  root.setProperty('--tblr-font-sans-serif', brand.font.family);
  document.body.style.fontFamily = brand.font.family;
}

// the Vite plugin rewrites the head at build time; this covers the runtime brand switcher
function applyHead(brand: Brand) {
  document.title = brand.name;

  const dir = brand.assetDir ?? '/';
  for (const { selector, file } of BRAND_ICONS) {
    const el = document.head.querySelector<HTMLLinkElement>(selector);
    if (el) el.href = `${dir}${file}`;
  }
}

export function applyBrandTheme(brand: Brand) {
  const root = document.documentElement.style;
  for (const [key, value] of Object.entries(brand.theme)) {
    for (const name of THEME_VARS[key as keyof BrandTheme]) root.setProperty(name, value);
  }

  const rgb = brand.theme.primary && hexToRgbTriplet(brand.theme.primary);
  if (rgb) root.setProperty('--tblr-primary-rgb', rgb);

  applyFont(brand);
  applyHead(brand);
}
